import React from "react";
import { Link } from "react-router-dom";
import { fmtDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { usePlatform } from "@/context/PlatformContext";
import ResponsiveDataView from "@/components/mobile/ResponsiveDataView";
import { MobileRecordCard, MobileRecordHeader, MobileRecordRow } from "@/components/mobile/MobileRecordCard";
import { useAuth } from "@/context/AuthContext";
import { ConsentProofButton, canGetConsentProof } from "@/components/leads/ConsentProofButton";

/**
 * Read-only table for the Leads list. Selection state is passed in via
 * `selected` + `setSelected` (a controlled dict keyed by lead id).
 *
 * Iter-53: When `hide_admin_numbers_global` is ON in platform settings,
 * the consumer phone column is masked with "•••" so operators can
 * screen-share safely.
 *
 * Iter-60: Renders as a wide `<table>` on desktop and as a stack of
 * mobile record cards on phones (<768px). Cards preserve every field
 * the desktop row shows for the caller's role — nothing is hidden or
 * revealed differently across viewports.
 */
/** Buyer the lead went to, or the buyer whose delivery failed (with why on hover). */
function RouteBuyer({ l }) {
  const r = l.route;
  if (!r) return "—";
  if (r.buyer) return <span className="font-semibold break-words">{r.buyer}</span>;
  if (r.held_buyer) return <span className="text-amber-800 break-words">Waiting for hours: {r.held_buyer}</span>;
  if (r.failed_buyer) {
    return <span className="text-red-700 break-words" title={r.failed_reason || ""}>{r.failed_retrying ? "Retrying" : "Failed"}: {r.failed_buyer}</span>;
  }
  return <span className="text-zinc-400">none</span>;
}

/** Firm the visitor's thank-you page named ("planned" when the visit wasn't recorded). */
function RouteThankYou({ l }) {
  const r = l.route;
  if (!r || !r.thankyou_firm) return <span className="text-zinc-400">—</span>;
  return (
    <span className="break-words">{r.thankyou_firm}{!r.thankyou_seen && <span className="text-zinc-400"> (planned)</span>}</span>
  );
}

export default function LeadsTable({
  leads, total, loading, selected, setSelected, showCheckbox,
}) {
  const platform = usePlatform();
  const maskNumbers = Boolean(platform?.hide_admin_numbers_global);
  const { user, hasPerm } = useAuth();
  const showProof = canGetConsentProof(user, hasPerm);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleAll = (e) => {
    const next = {};
    if (e.target.checked) leads.forEach((l) => { next[l.id] = true; });
    setSelected(next);
  };
  const colCount = (showCheckbox ? 13 : 12) + (showProof ? 1 : 0);

  const mobileCard = (l) => (
    <MobileRecordCard key={l.id} testId={`lead-card-${l.id}`}>
      <MobileRecordHeader
        title={
          <Link to={`/leads/${l.id}`} className="hover:underline" data-testid={`lead-link-${l.id}`}>
            {(l.first_name || l.last_name) ? `${l.first_name || ""} ${l.last_name || ""}`.trim() : l.id.slice(0, 8)}
            {l.is_test && <span className="ml-1 text-[9px] bg-yellow-400 text-black px-1">TEST</span>}
            {!l.is_test && l.extra?.test_sent_at && <span className="ml-1 text-[9px] border border-yellow-500 text-black px-1" data-testid={`lead-test-sent-mobile-${l.id}`}>TEST SENT</span>}
          </Link>
        }
        badge={<StatusBadge status={l.status} />}
      />
      <MobileRecordRow label="Lead" value={l.id.slice(0, 8)} />
      <MobileRecordRow
        label="Phone"
        value={maskNumbers ? <span className="text-zinc-400 tracking-widest">•••</span> : (l.phone || "—")}
        testId={`lead-phone-${l.id}`}
      />
      <MobileRecordRow label="Email" value={l.email || "—"} />
      <MobileRecordRow label="State" value={l.state || "—"} />
      <MobileRecordRow label="Source" value={l.public_source_id || l.affiliate_id?.slice(0, 8) || "—"} />
      <MobileRecordRow label="Delivery" value={<StatusBadge status={l.delivery_status} />} />
      {l.route && <MobileRecordRow label="Buyer" value={<RouteBuyer l={l} />} testId={`lead-buyer-${l.id}-mobile`} />}
      {l.route && <MobileRecordRow label="Landing page" value={l.route.landing_name || l.route.landing_page || "—"} testId={`lead-landing-${l.id}-mobile`} />}
      {l.route && <MobileRecordRow label="Thank-you" value={<RouteThankYou l={l} />} testId={`lead-thankyou-${l.id}-mobile`} />}
      <MobileRecordRow label="Received" value={fmtDate(l.created_at)} />
      {showProof && <MobileRecordRow label="Consent proof" value={<ConsentProofButton leadId={l.id} compact />} />}
      {showCheckbox && (
        <div className="mt-2 pt-2 border-t border-zinc-200 flex items-center gap-2">
          <input
            type="checkbox" data-testid={`lead-select-${l.id}`}
            checked={!!selected[l.id]}
            onChange={(e) => setSelected({ ...selected, [l.id]: e.target.checked })}
          />
          <span className="label-mono">Select</span>
        </div>
      )}
    </MobileRecordCard>
  );

  return (
    <div className="brut-card mt-4">
      <div className="flex items-center justify-between px-4 py-2 border-b border-black">
        <div className="label-mono">
          {loading ? "Loading…" : `${leads.length} shown · ${total} total`}
        </div>
        {maskNumbers ? (
          <div
            className="label-mono text-amber-700"
            data-testid="leads-table-screenshare-badge"
            title="Numbers masked platform-wide — toggle in /settings/platform"
          >
            SCREEN-SHARE MODE · NUMBERS MASKED
          </div>
        ) : (
          <div className="label-mono hidden md:block">FONT · IBM Plex Mono</div>
        )}
      </div>
      <div className="md:overflow-x-auto p-2 md:p-0">
        <ResponsiveDataView
          rows={leads}
          testId="leads-view"
          empty={<div className="py-8 text-center label-mono">No leads match your filters</div>}
          mobileCard={mobileCard}
          desktop={() => (
            <table className="w-full text-sm font-mono min-w-[1320px]">
              <thead>
                <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                  {showCheckbox && (
                    <th className="py-2 px-2 text-left">
                      <input type="checkbox" data-testid="lead-select-all"
                        onChange={toggleAll}
                        checked={selectedIds.length > 0 && selectedIds.length === leads.length}
                      />
                    </th>
                  )}
                  <th className="py-2 px-4 text-left">Lead ID</th>
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">Phone</th>
                  <th className="py-2 px-4 text-left">Email</th>
                  <th className="py-2 px-4 text-left">State</th>
                  <th className="py-2 px-4 text-left">Source</th>
                  <th className="py-2 px-4 text-left">Status</th>
                  <th className="py-2 px-4 text-left">Delivery</th>
                  <th className="py-2 px-4 text-left">Buyer</th>
                  <th className="py-2 px-4 text-left">Landing page</th>
                  <th className="py-2 px-4 text-left">Thank-you shown</th>
                  <th className="py-2 px-4 text-left">Received</th>
                  {showProof && <th className="py-2 px-4 text-left">Proof</th>}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}
                      className={`border-b border-zinc-200 hover:bg-zinc-100 ${l.is_archived ? "opacity-60" : ""}`}
                      data-testid={`lead-row-${l.id}`}>
                    {showCheckbox && (
                      <td className="py-2 px-2">
                        <input type="checkbox" data-testid={`lead-select-${l.id}`}
                          checked={!!selected[l.id]}
                          onChange={(e) => setSelected({ ...selected, [l.id]: e.target.checked })} />
                      </td>
                    )}
                    <td className="py-2 px-4">
                      <Link to={`/leads/${l.id}`} className="hover:underline" data-testid={`lead-link-${l.id}`}>
                        {l.id.slice(0, 8)}
                      </Link>
                      {l.is_archived && (
                        <span data-testid={`lead-archived-${l.id}`}
                              className="ml-1 text-[9px] uppercase tracking-widest bg-amber-100 border border-amber-700 text-amber-900 px-1">
                          archived
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-4">
                      {l.first_name} {l.last_name}
                      {l.is_test && <span className="ml-1 text-[9px] bg-yellow-400 text-black px-1">TEST</span>}
                      {!l.is_test && l.extra?.test_sent_at && <span className="ml-1 text-[9px] border border-yellow-500 text-black px-1" title="A real lead that was also sent to a buyer as a test" data-testid={`lead-test-sent-${l.id}`}>TEST SENT</span>}
                    </td>
                    <td className="py-2 px-4" data-testid={`lead-phone-${l.id}-desktop`}>
                      {maskNumbers ? (
                        <span className="text-zinc-400 tracking-widest">•••</span>
                      ) : (
                        l.phone || "—"
                      )}
                    </td>
                    <td className="py-2 px-4 max-w-[220px] truncate">{l.email || "—"}</td>
                    <td className="py-2 px-4">{l.state || "—"}</td>
                    <td className="py-2 px-4">{l.public_source_id || l.affiliate_id?.slice(0, 8) || "—"}</td>
                    <td className="py-2 px-4"><StatusBadge status={l.status} /></td>
                    <td className="py-2 px-4"><StatusBadge status={l.delivery_status} /></td>
                    <td className="py-2 px-4 max-w-[200px]" data-testid={`lead-buyer-${l.id}`}><RouteBuyer l={l} /></td>
                    <td className="py-2 px-4 max-w-[200px] truncate text-[11px]" data-testid={`lead-landing-${l.id}`}
                        title={l.route?.landing_page || ""}>{l.route?.landing_name || l.route?.landing_page || "—"}</td>
                    <td className="py-2 px-4 max-w-[200px] text-[11px]" data-testid={`lead-thankyou-${l.id}`}><RouteThankYou l={l} /></td>
                    <td className="py-2 px-4 text-[11px]">{fmtDate(l.created_at)}</td>
                    {showProof && <td className="py-2 px-4"><ConsentProofButton leadId={l.id} compact /></td>}
                  </tr>
                ))}
                {!leads.length && (
                  <tr><td colSpan={colCount} className="py-8 text-center label-mono">No leads match your filters</td></tr>
                )}
              </tbody>
            </table>
          )}
        />
      </div>
    </div>
  );
}
